/* eslint-disable @typescript-eslint/no-explicit-any */

import React, { useState, useEffect } from 'react';
import EditableField from '../component/EditableField';

import Car from "../../models/Car";
import type { AboutData } from '../../models/About';
import Icons from '../component/Icons';
import AppointmentRequestModal from '../component/AppointmentRequestModal';

// Business Constants for easy future modification/defaults
const BUSINESS_NAME = "AL Amir Auto Sales & Services";
const BUSINESS_ADDRESS_STREET = "295 37th St NE";
const BUSINESS_ADDRESS_CSZ = "Rochester, MN 55906";
const BUSINESS_PHONE = "(507) 850-5048";


interface HomeViewProps {
    content: AboutData | null;
    setContent: (content: AboutData) => void;
    isEditMode: boolean;
    inventory: Car[];
    navigate: (page: string) => void;
    onSelectCar: (car: Car) => void;
}

const HomeView: React.FC<HomeViewProps> = ({ content, setContent, isEditMode, inventory, navigate, onSelectCar } : HomeViewProps) => {
    const featuredCars = inventory.filter((c: Car) => c.featured && !c.archive);
    const [carouselIndex, setCarouselIndex] = useState(0);
    const [appointmentCar, setAppointmentCar] = useState<Car | null>(null);

     useEffect(() => {
        document.title = `${BUSINESS_NAME} | Quality Pre-Owned Vehicles Rochester, MN`;
        
        let metaDesc = document.querySelector('meta[name="description"]');
        if (!metaDesc) {
            metaDesc = document.createElement('meta');
            metaDesc.setAttribute('name', 'description');
            document.head.appendChild(metaDesc);
        }
        
        // Safely access deeply nested content with optional chaining
        const descriptionText : string = content?.aboutPage?.briefWelcome?.value || `Find high-quality used cars at ${BUSINESS_NAME} in Rochester, MN. Family owned with flexible financing.`;
        metaDesc.setAttribute("content", descriptionText);

        const updateMeta = (property: string, contentVal: string) => {
            let tag = document.querySelector(`meta[property="${property}"]`);
            if (!tag) {
                tag = document.createElement('meta');
                tag.setAttribute('property', property);
                document.head.appendChild(tag);
            }
            tag.setAttribute('content', contentVal);
        };

        updateMeta('og:title', `${BUSINESS_NAME} - Quality Pre-Owned Vehicles`);
        updateMeta('og:description', descriptionText);
        updateMeta('og:image', '/assets/logo.png'); 
        updateMeta('og:type', 'website');

        if (featuredCars.length > 1) {
            const timer = setInterval(() => {
                setCarouselIndex((prev) => (prev + 1) % featuredCars.length);
            }, 6000);
            return () => clearInterval(timer);
        }
    }, [featuredCars.length, content?.aboutPage?.briefWelcome?.value]);


    if(!content) {
        return (<div className="p-20 text-center text-gray-400 font-bold uppercase tracking-widest">Loading content...</div>);
    }
    
    // Derived values with fallbacks to constants
    const currentPhone = content.contact?.phoneSales?.value || BUSINESS_PHONE;
    const currentStreet = content.contact?.addressLine1?.value || BUSINESS_ADDRESS_STREET;
    const currentCSZ = content.contact?.addressLine2?.value || BUSINESS_ADDRESS_CSZ;
    const fullAddress = `${currentStreet}, ${currentCSZ}`;

    // Real Customer Reviews
    const realReviews = [
        { 
            name: "Abdullahi Mohammed", 
            text: "Truly the best experience buying a car in Rochester. No pressure, very honest about the vehicle history, and the prices are unbeatable. I love my new Camry!", 
            status: "Verified Buyer",
            stars: 5 
        },
        { 
            name: "Jessica Rivera", 
            text: "Found a perfect Honda Civic for my daughter. The owners are professional and treated us like family. It's refreshing to find a dealership this honest.", 
            status: "Recent Customer",
            stars: 5 
        },
        { 
            name: "Michael Thompson", 
            text: "Great selection and even better service. They helped me with financing and got me a rate I couldn't find anywhere else. Definitely check them out first.", 
            status: "Loyal Customer",
            stars: 5 
        },
        { 
            name: "Omar Hassan", 
            text: "I've bought two cars from Al Amir Auto over the years. They are consistent, reliable, and always have high-quality stock. The best in the area.", 
            status: "Verified Buyer",
            stars: 5 
        }
    ];

   
    const nextSlide = () => setCarouselIndex((prev) => (prev + 1) % featuredCars.length);
    const prevSlide = () => setCarouselIndex((prev) => (prev - 1 + featuredCars.length) % featuredCars.length);

    return (
        <div className="w-full flex-grow flex flex-col font-sans">
            <h1 className="sr-only">{BUSINESS_NAME} - Best Used Cars in Rochester MN</h1>

            {/* Hero Section */}
            <section className="relative bg-gray-900 text-white min-h-[750px] flex flex-col items-center justify-center overflow-hidden">
                <div className="absolute inset-0 opacity-50 bg-[url('https://images.unsplash.com/photo-1492144534655-ae79c964c9d7?q=80&w=2000&auto=format&fit=crop')] bg-cover bg-fixed bg-center"></div>
                <div className="absolute inset-0 bg-gradient-to-b from-gray-900 via-transparent to-gray-900"></div>
                
                <div className="relative z-10 max-w-5xl mx-auto px-4 text-center mb-12">
                    <div className="inline-block px-5 py-1.5 bg-red-600 text-[10px] font-black uppercase tracking-[0.4em] mb-8 animate-fade-in shadow-lg">
                        Family Owned & Operated
                    </div>
                    <EditableField 
                        contentKey="heroTitle" 
                        content={content} 
                        setContent={(updated: any) => setContent(updated)} 
                        isEditMode={isEditMode} 
                        as="h2" 
                        className="text-5xl md:text-8xl font-black italic uppercase tracking-tighter mb-6 drop-shadow-2xl leading-[0.9]" 
                    />
                    <EditableField 
                        contentKey="heroSubtitle" 
                        content={content} 
                        setContent={(updated: any) => setContent(updated)} 
                        isEditMode={isEditMode} 
                        as="p" 
                        className="text-lg md:text-2xl text-gray-200 mb-12 max-w-3xl mx-auto font-medium uppercase tracking-[0.2em] leading-relaxed" 
                    />
                    <div className="flex flex-col sm:flex-row gap-5 justify-center">
                        <button 
                            onClick={() => navigate('listings')} 
                            className="bg-red-600 hover:bg-red-700 text-white font-black uppercase tracking-widest py-6 px-12 rounded-none transform transition hover:-translate-y-1 shadow-2xl active:scale-95"
                        >
                            Browse Inventory
                        </button>
                        <a 
                            href={`tel:${currentPhone.replace(/\D/g, '')}`}
                            className="bg-white hover:bg-gray-100 text-gray-900 font-black uppercase tracking-widest py-6 px-12 rounded-none transform transition hover:-translate-y-1 shadow-2xl active:scale-95"
                        >
                            Call Sales Dept
                        </a>
                    </div>
                </div>

                {/* Quick Info Bar - Fully Editable */}
                <div className="relative z-20 w-full max-w-7xl px-4 mt-auto mb-10">
                    <div className="grid grid-cols-1 md:grid-cols-3 bg-white/95 backdrop-blur-md rounded-3xl shadow-2xl overflow-hidden ring-1 ring-white/20">
                        {/* Hours */}
                        <div className="p-8 border-b md:border-b-0 md:border-r border-gray-100 flex items-start space-x-6">
                            <div className="text-red-600 w-10 flex-shrink-0 mt-1"><Icons.Menu /></div>
                            <div>
                                <h4 className="font-black italic uppercase tracking-widest text-gray-900 mb-3 text-lg">Our Hours</h4>
                                <ul className="text-sm font-bold text-gray-600 space-y-1 uppercase tracking-tight">
                                    <li className="flex justify-between space-x-4">
                                        <span>Mon - Fri</span> 
                                        <EditableField 
                                            contentKey="businessHoursMonFri" 
                                            content={content} 
                                            setContent={setContent} 
                                            isEditMode={isEditMode} 
                                            className="text-gray-900 text-right"
                                        />
                                    </li>
                                    <li className="flex justify-between space-x-4">
                                        <span>Sat</span> 
                                        <EditableField 
                                            contentKey="businessHoursSat" 
                                            content={content} 
                                            setContent={setContent} 
                                            isEditMode={isEditMode} 
                                            className="text-gray-900 text-right"
                                        />
                                    </li>
                                    <li className="flex justify-between space-x-4 text-red-600 font-black">
                                        <span>Sunday</span> 
                                        <EditableField 
                                            contentKey="businessHoursSun" 
                                            content={content} 
                                            setContent={setContent} 
                                            isEditMode={isEditMode} 
                                            className="text-right"
                                        />
                                    </li>
                                </ul>
                            </div>
                        </div>
                        {/* Phone */}
                        <div className="p-8 border-b md:border-b-0 md:border-r border-gray-100 flex items-start space-x-6">
                            <div className="text-red-600 w-10 flex-shrink-0 mt-1"><Icons.Dollar /></div>
                            <div>
                                <h4 className="font-black italic uppercase tracking-widest text-gray-900 mb-3 text-lg">Quick Contact</h4>
                                <div className="block text-2xl font-black text-gray-900 hover:text-red-600 transition tracking-tighter">
                                    <EditableField 
                                        contentKey="businessPhone" 
                                        content={content} 
                                        setContent={setContent} 
                                        isEditMode={isEditMode} 
                                    />
                                </div>
                                <p className="text-[10px] font-black uppercase text-gray-400 tracking-widest mt-1">Direct Sales & Support</p>
                            </div>
                        </div>
                        {/* Location */}
                        <div className="p-8 flex items-start space-x-6">
                            <div className="text-red-600 w-10 flex-shrink-0 mt-1"><Icons.Dashboard /></div>
                            <div className="flex-grow">
                                <h4 className="font-black italic uppercase tracking-widest text-gray-900 mb-3 text-lg">Our Showroom</h4>
                                <div className="text-sm font-bold text-gray-600 leading-snug uppercase">
                                    <EditableField 
                                        contentKey="businessAddressStreet" 
                                        content={content} 
                                        setContent={setContent} 
                                        isEditMode={isEditMode} 
                                    />
                                    <br />
                                    <EditableField 
                                        contentKey="businessAddressCSZ" 
                                        content={content} 
                                        setContent={setContent} 
                                        isEditMode={isEditMode} 
                                    />
                                </div>
                                <a 
                                    href={`https://www.google.com/maps/dir//${encodeURIComponent(fullAddress)}`} 
                                    target="_blank" 
                                    rel="noreferrer" 
                                    className="inline-block mt-3 text-[10px] font-black text-red-600 uppercase tracking-widest border-b-2 border-red-600/20 hover:border-red-600 transition"
                                >
                                    Get Directions
                                </a>
                            </div>
                        </div>
                    </div>
                </div>
                <div className="h-2 w-full bg-red-600 mt-0"></div>
            </section>

            {/* Featured Carousel Section */}
            <section className="py-24 bg-white overflow-hidden">
                <div className="max-w-7xl mx-auto px-4">
                    <div className="flex flex-col md:flex-row justify-between items-end mb-16 gap-4">
                        <div>
                            <span className="text-red-600 font-black uppercase tracking-widest text-xs">Hand Picked</span>
                            <h2 className="text-5xl font-black italic uppercase tracking-tighter text-gray-900 leading-none mt-2">Featured Vehicles</h2>
                            <div className="h-1.5 w-24 bg-red-600 mt-4"></div>
                        </div>
                        <button onClick={() => navigate('listings')} className="group text-sm font-black uppercase tracking-widest text-gray-400 hover:text-red-600 transition flex items-center">
                            See Full Inventory <span className="ml-2 group-hover:translate-x-2 transition-transform">&rarr;</span>
                        </button>
                    </div>

                    {featuredCars.length > 0 ? (
                        <div className="relative group">
                            <div className="overflow-hidden rounded-[2rem]">
                                <div 
                                    className="flex transition-transform duration-1000 cubic-bezier(0.4, 0, 0.2, 1)" 
                                    style={{ transform: `translateX(-${carouselIndex * 100}%)` }}
                                >
                                    {featuredCars.map((car) => (
                                        <div key={car.id} className="w-full flex-shrink-0 px-1">
                                            <div className="grid grid-cols-1 lg:grid-cols-2 gap-0 lg:gap-12 items-center bg-gray-50 rounded-[2rem] overflow-hidden border border-gray-100 shadow-xl lg:p-8">
                                                <div className="h-[350px] md:h-[500px] relative lg:rounded-3xl overflow-hidden shadow-2xl">
                                                    <img 
                                                        src={car.images?.[0] || 'https://placehold.co/800x600/cccccc/ffffff?text=No+Image'} 
                                                        alt={`${car.year} ${car.make} ${car.model}`} 
                                                        className="w-full h-full object-cover transition-transform duration-[2000ms] group-hover:scale-110"
                                                    />
                                                    <div className="absolute top-6 left-6 bg-red-600 text-white text-[10px] font-black uppercase px-4 py-2 shadow-lg">FEATURED</div>
                                                </div>
                                                <div className="flex flex-col p-8 lg:p-4">
                                                    <h3 className="text-6xl font-black italic uppercase tracking-tighter text-gray-900 leading-none mb-2">
                                                        {car.year} {car.make}
                                                    </h3>
                                                    <p className="text-3xl font-bold text-red-600 uppercase tracking-[0.2em] mb-10">{car.model}</p>
                                                    <div className="grid grid-cols-2 gap-6 mb-10">
                                                        <div className="bg-white p-6 rounded-2xl border border-gray-100 shadow-sm flex flex-col">
                                                            <span className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-1">Total Miles</span>
                                                            <p className="text-2xl font-black text-gray-800 tracking-tighter">{car.mileage?.toLocaleString()} mi</p>
                                                        </div>
                                                        <div className="bg-white p-6 rounded-2xl border border-gray-100 shadow-sm flex flex-col">
                                                            <span className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-1">Our Price</span>
                                                            <p className="text-2xl font-black text-green-600 tracking-tighter">${car.price?.toLocaleString()}</p>
                                                        </div>
                                                    </div>
                                                    <button 
                                                        onClick={() => onSelectCar(car)}
                                                        className="w-full bg-gray-900 text-white font-black uppercase tracking-widest py-6 rounded-2xl hover:bg-black transition-all shadow-xl shadow-gray-200 transform hover:-translate-y-1 mb-4"
                                                    >
                                                        View Full Details
                                                    </button>
                                                    <button 
                                                        onClick={() => setAppointmentCar(car)}
                                                        className="w-full bg-red-600 text-white font-black uppercase tracking-widest py-6 rounded-2xl hover:bg-red-700 transition-all shadow-xl shadow-red-100 transform hover:-translate-y-1"
                                                    >
                                                        Request Showing
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                            
                            <button onClick={prevSlide} className="absolute left-6 top-1/2 -translate-y-1/2 bg-white shadow-2xl text-gray-900 w-14 h-14 rounded-full flex items-center justify-center hover:bg-red-600 hover:text-white transition-all z-10 opacity-0 group-hover:opacity-100 transform -translate-x-1/2">
                                &#10094;
                            </button>
                            <button onClick={nextSlide} className="absolute right-6 top-1/2 -translate-y-1/2 bg-white shadow-2xl text-gray-900 w-14 h-14 rounded-full flex items-center justify-center hover:bg-red-600 hover:text-white transition-all z-10 opacity-0 group-hover:opacity-100 transform translate-x-1/2">
                                &#10095;
                            </button>
                            
                            <div className="flex justify-center space-x-3 mt-12">
                                {featuredCars.map((_, idx) => (
                                    <button 
                                        key={idx} 
                                        onClick={() => setCarouselIndex(idx)}
                                        className={`h-2 transition-all duration-500 rounded-full ${idx === carouselIndex ? 'w-12 bg-red-600' : 'w-2 bg-gray-300'}`}
                                    />
                                ))}
                            </div>
                        </div>
                    ) : (
                        <p className="text-center py-24 text-gray-400 font-bold uppercase tracking-widest bg-gray-50 rounded-3xl border-2 border-dashed border-gray-200">No featured listings currently available</p>
                    )}
                </div>
            </section>

            {/* Brand Values Section */}
            <section className="py-32 bg-gray-900 text-white">
                <div className="max-w-7xl mx-auto px-4">
                    <div className="text-center max-w-4xl mx-auto mb-20">
                        <EditableField 
                            contentKey="homeAboutTitle" 
                            content={content} 
                            setContent={(updated: any) => setContent(updated)} 
                            isEditMode={isEditMode} 
                            as="h2" 
                            className="text-5xl font-black italic uppercase tracking-tighter mb-6" 
                        />
                        <div className="h-1.5 w-20 bg-red-600 mx-auto mb-8"></div>
                        <EditableField 
                            contentKey="homeAboutText" 
                            content={content} 
                            setContent={(updated: any) => setContent(updated)} 
                            isEditMode={isEditMode} 
                            as="p" 
                            className="text-gray-400 text-xl leading-relaxed tracking-wide font-light" 
                        />
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-16">
                        <div className="flex flex-col items-center text-center group">
                            <div className="w-24 h-24 bg-gray-800 text-red-500 rounded-3xl flex items-center justify-center mb-8 border border-gray-700 group-hover:border-red-600 transition-all duration-500 transform group-hover:-rotate-6 group-hover:scale-110 shadow-2xl">
                                <Icons.Shield />
                            </div>
                            <h3 className="text-2xl font-black uppercase tracking-widest mb-4 italic">Certified Quality</h3>
                            <p className="text-gray-500 text-sm leading-relaxed max-w-xs">Every vehicle undergoes a multi-point safety inspection to ensure reliability and peace of mind.</p>
                        </div>
                        <div className="flex flex-col items-center text-center group">
                            <div className="w-24 h-24 bg-gray-800 text-red-500 rounded-3xl flex items-center justify-center mb-8 border border-gray-700 group-hover:border-red-600 transition-all duration-500 transform group-hover:-rotate-6 group-hover:scale-110 shadow-2xl">
                                <Icons.Dollar />
                            </div>
                            <h3 className="text-2xl font-black uppercase tracking-widest mb-4 italic">Fair Financing</h3>
                            <p className="text-gray-500 text-sm leading-relaxed max-w-xs">Transparent terms with competitive rates. We work with local credit unions to save you money.</p>
                        </div>
                        <div className="flex flex-col items-center text-center group">
                            <div className="w-24 h-24 bg-gray-800 text-red-500 rounded-3xl flex items-center justify-center mb-8 border border-gray-700 group-hover:border-red-600 transition-all duration-500 transform group-hover:-rotate-3 group-hover:scale-110 shadow-2xl">
                                <Icons.Car />
                            </div>
                            <h3 className="text-2xl font-black uppercase tracking-widest mb-4 italic">Trusted Legacy</h3>
                            <p className="text-gray-500 text-sm leading-relaxed max-w-xs">Family owned and operated. Our reputation is built on honesty and community trust.</p>
                        </div>
                    </div>
                </div>
            </section>

            {/* Meet the Family Section */}
            <section className="py-24 bg-white">
                <div className="max-w-7xl mx-auto px-4">
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
                        <div className="relative">
                            <div className="absolute -top-6 -left-6 w-32 h-32 bg-red-600 z-0 rounded-3xl opacity-10"></div>
                            <img 
                                src="/assets/owners.png" 
                                alt={`The ${BUSINESS_NAME} Family`} 
                                className="relative z-10 rounded-[3rem] shadow-2xl w-full h-auto object-cover border-8 border-gray-50"
                                onError={(e) => {
                                    (e.target as HTMLImageElement).src = "https://images.unsplash.com/photo-1511632765486-a01980e01a18?q=80&w=1200&auto=format&fit=crop";
                                }}
                            />
                            <div className="absolute -bottom-10 -right-4 bg-gray-900 text-white p-10 rounded-[2.5rem] shadow-2xl z-20 hidden md:block border-t-4 border-red-600">
                                <p className="text-5xl font-black italic uppercase leading-none mb-2 tracking-tighter">25+ YRS</p>
                                <p className="text-[10px] font-black uppercase tracking-[0.4em] text-red-500">Service Legacy</p>
                            </div>
                        </div>
                        <div className="lg:pl-10">
                            <span className="text-red-600 font-black uppercase tracking-widest text-xs">A Rochester Staple</span>
                            <h2 className="text-5xl font-black italic uppercase tracking-tighter text-gray-900 mt-2 mb-8 leading-tight">Built on Family Values</h2>
                            <p className="text-gray-600 text-lg leading-relaxed mb-10 font-medium">
                                {BUSINESS_NAME} isn't just a dealership; it's our family legacy.  We've provided Rochester with more than just cars—we've provided honesty, transparency, and a commitment to the community that you won't find anywhere else.
                            </p>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                                <div className="flex items-center space-x-4 p-5 bg-gray-50 rounded-2xl border border-gray-100 group hover:bg-white hover:shadow-xl transition-all">
                                    <div className="w-12 h-12 bg-gray-900 text-white rounded-xl flex items-center justify-center font-black group-hover:bg-red-600 transition-colors italic text-xl">01</div>
                                    <p className="font-bold text-gray-800 text-sm leading-tight uppercase">Direct Owner<br/>Involvement</p>
                                </div>
                                <div className="flex items-center space-x-4 p-5 bg-gray-50 rounded-2xl border border-gray-100 group hover:bg-white hover:shadow-xl transition-all">
                                    <div className="w-12 h-12 bg-gray-900 text-white rounded-xl flex items-center justify-center font-black group-hover:bg-red-600 transition-colors italic text-xl">02</div>
                                    <p className="font-bold text-gray-800 text-sm leading-tight uppercase">No-Gimmick<br/>Pricing</p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Real Reviews Section */}
            <section className="py-24 bg-gray-50 border-t border-gray-100">
                <div className="max-w-7xl mx-auto px-4">
                    <div className="text-center mb-20">
                        <span className="text-red-600 font-black uppercase tracking-widest text-xs">Customer Satisfaction</span>
                        <h2 className="text-5xl font-black italic uppercase tracking-tighter text-gray-900 mt-2">Verified Testimonials</h2>
                        <div className="flex justify-center mt-6 space-x-1">
                            {[1, 2, 3, 4, 5].map(i => <span key={i} className="text-yellow-400 text-3xl">★</span>)}
                        </div>
                        <p className="text-gray-400 font-bold uppercase text-[10px] tracking-[0.3em] mt-4">Verified 4.9 Star Average on Google Reviews</p>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
                        {realReviews.map((review, i) => (
                            <div key={i} className="bg-white p-8 rounded-[2rem] border border-gray-100 relative shadow-sm hover:shadow-xl transition-all flex flex-col group transform hover:-translate-y-2">
                                <div className="text-red-600 text-6xl absolute top-4 left-4 opacity-5 font-serif">"</div>
                                <div className="flex text-yellow-400 text-sm mb-4">
                                    {[...Array(review.stars)].map((_, j) => <span key={j}>★</span>)}
                                </div>
                                <p className="text-gray-700 italic text-sm leading-relaxed mb-6 relative z-10 flex-grow">“{review.text}”</p>
                                <div className="border-t border-gray-200 pt-4">
                                    <p className="font-black uppercase text-xs text-gray-900 tracking-widest">{review.name}</p>
                                    <p className="text-[9px] font-bold text-gray-400 uppercase tracking-widest mt-1">{review.status}</p>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Map Section */}
            <section className="py-32 bg-white">
                <div className="max-w-7xl mx-auto px-4">
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-20 items-center">
                        <div className="order-2 lg:order-1">
                            <h2 className="text-5xl font-black italic uppercase tracking-tighter text-gray-900 mb-10">Visit Our Showroom</h2>
                            <div className="space-y-10">
                                <div className="flex items-start space-x-6 group">
                                    <div className="w-12 h-12 bg-white text-red-600 rounded-2xl flex items-center justify-center shadow-lg group-hover:bg-red-600 group-hover:text-white transition-all border border-gray-100"><Icons.Dashboard /></div>
                                    <div>
                                        <p className="font-black uppercase text-[10px] text-gray-400 tracking-[0.3em] mb-2">Our HQ Location</p>
                                        <p className="text-xl font-bold text-gray-800 leading-tight">
                                            {!isEditMode && !content ? BUSINESS_ADDRESS_STREET : currentStreet},<br />
                                            {!isEditMode && !content ? BUSINESS_ADDRESS_CSZ : currentCSZ}
                                        </p>
                                    </div>
                                </div>
                                <div className="flex items-start space-x-6 group">
                                    <div className="w-12 h-12 bg-white text-red-600 rounded-2xl flex items-center justify-center shadow-lg group-hover:bg-red-600 group-hover:text-white transition-all border border-gray-100"><Icons.Menu /></div>
                                    <div>
                                        <p className="font-black uppercase text-[10px] text-gray-400 tracking-[0.3em] mb-2">Business Hours</p>
                                        <p className="text-xl font-bold text-gray-800 mb-1">
                                            {!isEditMode && !content ? "Mon - Fri: 9:00 AM - 7:00 PM" : `Mon - Fri: ${content?.hours?.mf?.value || '9AM - 7PM' }`}
                                        </p>
                                        <p className="text-xl font-bold text-gray-800 mb-1">
                                            {!isEditMode && !content ? "Saturday: 9:00 AM - 7:00 PM" : `Saturday: ${ content?.hours?.sat?.value || '9AM - 7PM'}`}
                                        </p>
                                        <p className="text-gray-400 font-bold uppercase text-xs">
                                            {!isEditMode && !content ? "Sunday: Closed for Family" : `Sunday: ${ content?.hours?.sun?.value || 'Closed'}`}
                                        </p>
                                    </div>
                                </div>
                            </div>
                            <div className="mt-14">
                                <a 
                                    href={`https://www.google.com/maps/dir//${encodeURIComponent(fullAddress)}`} 
                                    target="_blank" 
                                    rel="noreferrer"
                                    className="inline-flex items-center bg-gray-900 text-white font-black uppercase tracking-[0.2em] py-6 px-10 rounded-2xl hover:bg-black transition-all shadow-2xl active:scale-95"
                                >
                                    Get Driving Directions
                                </a>
                            </div>
                        </div>
                        <div className="order-1 lg:order-2 h-[550px] bg-white rounded-[3rem] overflow-hidden shadow-2xl border-[12px] border-white ring-1 ring-gray-200">
                            <iframe 
                                title="Al Amir Auto Location"
                                src={`https://www.google.com/maps?q=${encodeURIComponent(BUSINESS_NAME + " " + fullAddress)}&t=&z=15&ie=UTF8&iwloc=&output=embed`}
                                width="100%" 
                                height="100%" 
                                style={{ border: 0 }} 
                                allowFullScreen={true} 
                                loading="lazy" 
                                referrerPolicy="no-referrer-when-downgrade"
                            ></iframe>
                        </div>
                    </div>
                </div>
            </section>

            {appointmentCar && (
                <AppointmentRequestModal
                    car={appointmentCar}
                    mode="guest-request"
                    onClose={() => setAppointmentCar(null)}
                />
            )}
        </div>
    );
};

export default HomeView;